// ✅ Importaciones necesarias
import { useRef, useState } from 'react';
import { ButtonSiguiente } from '../../ui';
import { useNavigate } from "react-router-dom";
import { safeAxios } from '@/api/safeAxios';
import { Eye, EyeOff } from 'lucide-react';
import { Usuario } from '@/types';

interface LoginFormProps {
  onLoginSuccess?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // 🔸 Evento principal: envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);

    try {
      const response = await safeAxios("/api/login", navigate, {
        method: "POST",
        data: { email, contrasena }
      });

      const data = response.data;

      // 🔍 Validación correcta según backend REAL
      if (!data || !data.usuario_id) {
        console.error("No viene usuario_id en la respuesta:", data);
        setErrorMessage("Error inesperado. Intenta nuevamente.");
        return;
      }

      // 🟢 Guardamos datos individuales
      localStorage.setItem("token", data.token);
      localStorage.setItem("usuario_id", String(data.usuario_id));
      localStorage.setItem("username", data.username);
      localStorage.setItem("email", data.email);
      localStorage.setItem("roles", JSON.stringify(data.roles));
      localStorage.setItem("verificado", String(data.verificado));
      localStorage.setItem("afiliado_id", String(data.afiliado_id));
      localStorage.setItem("profesional_id", String(data.profesional_id));
      localStorage.setItem("suscripcion", JSON.stringify(data.suscripcion));

      // 🟦 Construimos el objeto Usuario completo
      const usuario: Usuario = {
        usuario_id: data.usuario_id,
        nombre_usuario: data.username,
        email: data.email,

        roles: data.roles,
        verificado: data.verificado,

        afiliado_id: data.afiliado_id,
        profesional_id: data.profesional_id,

        suscripcion: data.suscripcion,

        // Flags derivados
        esAfiliado: Boolean(data.afiliado_id),
        esProfesional: Boolean(data.profesional_id),
      };

      // Guardamos el objeto completo
      localStorage.setItem("usuario", JSON.stringify(usuario));

      window.dispatchEvent(new Event("tokenChanged"));
      onLoginSuccess?.();

      // 🔀 Redirección según roles
      const roles = Array.isArray(data.roles) ? data.roles : [];

      if (roles.includes("admin") || roles.includes("superadmin")) {
        navigate("/backoffice", { replace: true });
      } else {
        navigate("/", { replace: true });
      }

    } catch (err: any) {

      if (err.response?.status === 403) {
        setErrorMessage("Tu cuenta está deshabilitada. Contacta con un SuperAdmin.");
        return;
      }

      if (err.response?.status === 401) {
        setErrorMessage("Usuario o contraseña incorrectos");
        return;
      }

      setErrorMessage(
        err.response?.data?.message ||
        "Error de conexión. Inténtalo de nuevo."
      );

    } finally {
      setLoading(false);
    }
  };

  // 🧱 Render del formulario
  return (
    <form
      className="flex flex-col justify-between h-full pt-8"
      onSubmit={handleSubmit}
    >

      {/* Campo de correo o usuario */}
      <div className="relative border-b border-white focus-within:border-white">
        <input
          type="text"
          id="email"
          placeholder="Correo / Usuario"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-black text-zinc-100 placeholder:text-zinc-500 outline-none py-2 placeholder-white focus:placeholder-transparent pl-2 "
        />
      </div>

      {/* Campo de contraseña */}
      <div className="relative border-b border-white focus-within:border-white my-4">
        <input
          type="text"
          id="contrasena"
          placeholder="Contraseña"
          ref={inputRef}
          required
          value={contrasena}
          onChange={(e) => setContrasena(e.target.value)}
          className={`w-full text-white bg-black outline-none py-2 placeholder-white focus:placeholder-transparent pl-2 pr-10 rounded-md ${!showPassword ? "password-hidden" : ""
            }`}
          autoComplete="current-password"
        />

        <button
          type="button"
          onClick={togglePasswordVisibility}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-700 bg-transparent border-none p-0"
        >
          <span className="pointer-events-none">
            {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
          </span>
        </button>
      </div>

      {/* Mensaje de error debajo del campo contraseña */}
      {errorMessage && (
        <p className="text-red-500 text-sm mb-2 animate-fadeIn">
          {errorMessage}
        </p>
      )}

      {/* Enlace para recuperar contraseña */}
      <button
        type="button"
        onClick={() => {
          onLoginSuccess?.(); // 🔥 CIERRA MODAL
          setTimeout(() => {
            navigate("/codigoRecuperacion"); // 🔥 NAVEGA DESPUÉS
          }, 0);
        }}
        className="text-white text-start hover:text-blue-700"
      >
        Recupera contraseña
      </button>


      {/* Botón de inicio de sesión */}
      <ButtonSiguiente
        type="submit"
        className="w-full mt-4 font-bold hover:bg-gray-900 p-3"
      >
        {loading ? (
          <div className="flex justify-center items-center">
            Cargando...
          </div>
        ) : (
          'Acceder'
        )}
      </ButtonSiguiente>

    </form>
  );
};

// Exporta el componente
export default LoginForm;

